import { ShieldCheck } from 'lucide-react';
import { useMemo, useState, type ReactElement } from 'react';

import { createAdminApi } from '@/admin/adminApi';
import { useHttpClient } from '@/app/providers';
import { Button, Card, LoadingState } from '@/ui/kit';

import { IntegrityChecks } from './IntegrityChecks';
import { ProblemView } from './ProblemView';
import { describeStockProblem } from './stockPresentation';
import { useServerRead } from './useServerRead';

/**
 * Controles d'integrite de **tout le perimetre** du lot (le compte, ADR 0037).
 *
 * Ceux d'un lot viennent avec sa trace ; ceux-ci rejouent les sept controles sur
 * tous les lots, et c'est ce qui rend utiles les liens vers les lots en cause. Le
 * serveur rejoue tout le journal du perimetre a chaque appel et ne garde aucun
 * resultat : la lecture n'est donc lancee que **sur un geste** (« Contrôler le
 * périmètre »), jamais a l'ouverture de l'onglet, et chaque lecture ecrit une
 * ligne d'audit.
 */
export const ScopeIntegrity = ({
  scopeId,
  currentBatchId,
  codes,
}: {
  scopeId: string;
  currentBatchId: string;
  codes: ReadonlyMap<string, string | null>;
}): ReactElement => {
  const http = useHttpClient();
  const api = useMemo(() => createAdminApi(http), [http]);
  const [requested, setRequested] = useState(false);

  const read = useMemo(() => (requested ? () => api.getStockIntegrity(scopeId) : null), [api, scopeId, requested]);
  const { state, reload } = useServerRead(read);
  const report = state.phase === 'idle' ? null : state.data;

  return (
    <>
      {report === null ? (
        <Card
          title="Contrôle de tout le périmètre"
          icon={ShieldCheck}
          subtitle="Rejoue les sept contrôles sur tous les lots du compte, pas seulement sur celui-ci."
          actions={
            <Button variant="secondary" disabled={requested} onClick={() => { setRequested(true); }}>
              Contrôler le périmètre
            </Button>
          }
        >
          {state.phase === 'loading' ? <LoadingState label="Contrôle du périmètre en cours…" /> : null}
        </Card>
      ) : (
        <IntegrityChecks
          title="Contrôle de tout le périmètre"
          icon={ShieldCheck}
          subtitle="Les sept contrôles, rejoués sur tous les lots du compte à votre demande."
          listLabel="Contrôles d’intégrité du périmètre"
          checks={report.checks}
          currentBatchId={currentBatchId}
          codes={codes}
          scopeNote={`${String(report.batchCount)} ${report.batchCount > 1 ? 'lots contrôlés' : 'lot contrôlé'}.`}
        />
      )}
      {state.phase === 'failed' ? <ProblemView problem={describeStockProblem(state.error)} onRetry={reload} /> : null}
    </>
  );
};
